import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { WBSFullWidthButton } from '@/common/WBSButton';
import { useRegister } from '~/hooks/auth/useRegister';
import {
  AuthCheckbox,
  AuthContainer,
  AuthField,
  AuthHeading,
  AuthHeadingLink,
  AuthHeadingText,
  AuthLabel,
  AuthLink,
  AuthLogoLink,
  RequiredAsterisk,
} from '~/styles/authStyles';

interface RegisterSearch {
  campaign?: string;
}

export const Route = createFileRoute('/_auth/register')({
  component: RegisterComponent,
  // `?campaign=<handle>` is what a campaign's shareable sign-up link carries
  // (see the backend's CampaignItem.signup_url).
  validateSearch: (search: Record<string, unknown>): RegisterSearch => ({
    campaign: typeof search.campaign === 'string' ? search.campaign : undefined,
  }),
});

function RegisterComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const register = useRegister();
  const { campaign } = Route.useSearch();
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Open a connect session for the campaign as soon as the page loads, so the
  // token is already in hand when the form is submitted. An unknown handle 404s
  // and is ignored — registration still works, just without a campaign link.
  const { data: campaignSession } = api.createCampaignSession.useQuery(
    { query: { domain: campaign ?? '' } },
    { enabled: !!campaign, retry: false },
  );

  return (
    <AuthContainer>
      <AuthLogoLink to="/">
        <img
          alt={t('wbs.world-bible-school')}
          src="https://worldbibleschool.org/wp-content/uploads/2020/06/stack_logo-EN-200w.png"
        />
      </AuthLogoLink>
      <AuthHeading>
        {/* Translation Needed */}
        <AuthHeadingText>Student Registration</AuthHeadingText>
        <AuthHeadingLink to="/login">{t('wbs.login')}</AuthHeadingLink>
      </AuthHeading>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          const formData = new FormData(e.currentTarget);
          const d = Object.fromEntries(formData);
          await register(
            d.firstName as string,
            d.lastName as string,
            d.email as string,
            d.password as string,
            d.confirmPassword as string,
            campaignSession?.token,
          );
          navigate({ to: '/login/' });
        }}
      >
        <FormFieldsContainer>
          <div>
            <AuthLabel>
              {t('wbs.name')} <RequiredAsterisk>*</RequiredAsterisk>
            </AuthLabel>
            <NameFieldsWrapper>
              <NameFieldContainer>
                <AuthField name="firstName" type="text" required />
                <FieldHint>{t('wbs.first')}</FieldHint>
              </NameFieldContainer>
              <NameFieldContainer>
                <AuthField name="lastName" id="lastName" type="text" required />
                <FieldHint>{t('wbs.last')}</FieldHint>
              </NameFieldContainer>
            </NameFieldsWrapper>
          </div>
          <div>
            <AuthLabel htmlFor="email">{t('notifications.email')}</AuthLabel>
            <AuthField
              id="email"
              name="email"
              type="email"
              required
              fullWidth
            />
          </div>
          <div>
            <AuthLabel>
              {t('wbs.password')} <RequiredAsterisk>*</RequiredAsterisk>
            </AuthLabel>
            <PasswordFieldContainer>
              <AuthField name="password" type="password" required fullWidth />
              {/* Translation Needed */}
              <FieldHint>Enter Password</FieldHint>
            </PasswordFieldContainer>

            <PasswordFieldContainer>
              <AuthField
                name="confirmPassword"
                id="confirmPassword"
                type="password"
                required
                fullWidth
              />
              {/* Translation Needed */}
              <FieldHint>Confirm Password</FieldHint>
            </PasswordFieldContainer>
          </div>
        </FormFieldsContainer>

        <TermsWrapper>
          <TermsLabel htmlFor="terms">
            <AuthCheckbox
              id="terms"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              required
            />
            <span>
              {/* Translation Needed */}
              {/* profile.by-creating-an-account is similar but need something adjusted for this setup */}
              By creating an account you are agreeing to the{' '}
              <AuthLink to="/terms">Terms of Service</AuthLink> and{' '}
              <AuthLink to="/privacy">{t('wbs.privacy-policy')}</AuthLink>.{' '}
              <RequiredAsterisk>*</RequiredAsterisk>
            </span>
          </TermsLabel>
        </TermsWrapper>

        <WBSFullWidthButton type="submit" wide>
          {t('actions.create-student-account')}
        </WBSFullWidthButton>
      </form>
    </AuthContainer>
  );
}
const FormFieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const NameFieldsWrapper = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 14px;
  }
`;

const PasswordFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
  ${AuthField} {
    margin-bottom: 5px;
  }
`;

const NameFieldContainer = styled.div`
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  min-width: 0;

  ${AuthField} {
    margin-bottom: 5px;
  }
`;

const FieldHint = styled.span`
  color: var(--black);
  font-family: var(--raleway);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.7px;
`;

const TermsWrapper = styled.div`
  align-items: flex-start;
  display: flex;
  margin-bottom: 20px;
  margin-top: 10px;
`;

const TermsLabel = styled.label`
  align-items: flex-start;
  color: var(--black);
  cursor: pointer;
  display: flex;
  font-family: Raleway, sans-serif;
  font-size: 13px;
  gap: 8px;
  line-height: 1.5;

  @media (max-width: 768px) {
    gap: 10px;
  }
`;
